import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import paymentTypeService, { CreatePaymentTypeDTO, PaymentCategory, PaymentUsageScope } from '../../../../services/paymentTypeService';
import '../../../../styles/ImsTheme.css';
import './PaymentTypeFormPage.css';
import { Save, ArrowLeft } from 'lucide-react';
import { parseApiError } from '../../../../utils/errorUtils';

const SCOPE_OPTIONS: { value: PaymentUsageScope; icon: string; label: string; hint: string; color: string }[] = [
  { value: 'Sales',    icon: '🛒', label: 'Sales',    color: '#15803d', hint: 'Appears only in sales invoices and POS.' },
  { value: 'Purchase', icon: '📦', label: 'Purchase', color: '#1d4ed8', hint: 'Appears only in Purchase Orders and GRN.' },
  { value: 'Both',     icon: '🔄', label: 'Both',     color: '#7c3aed', hint: 'Appears in both sales and purchasing flows.' },
];

const CATEGORY_OPTIONS: { value: PaymentCategory; icon: string; label: string; hint: string; color: string }[] = [
  { value: 'Cash',   icon: '💵', label: 'Cash',   color: '#15803d', hint: 'Physical cash payments. Only one Cash type is allowed.' },
  { value: 'Credit', icon: '🏦', label: 'Credit', color: '#1d4ed8', hint: 'Customer on-account credit. Only one Credit type is allowed.' },
  { value: 'Other',  icon: '💳', label: 'Other',  color: '#7c3aed', hint: 'Card, voucher, bank transfer, etc. Multiple types allowed.' },
];

const PaymentTypeCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usedSingletons, setUsedSingletons] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<CreatePaymentTypeDTO>({
    code: '',
    name: '',
    description: '',
    paymentCategory: 'Other',
    usageScope: 'Both',
    isDefault: false,
    sortOrder: 0,
  });

  // Load existing active types to detect which singleton categories are already taken
  useEffect(() => {
    paymentTypeService.getActivePaymentTypes().then((types) => {
      const taken = new Set(
        types
          .filter((pt) => pt.paymentCategory === 'Cash' || pt.paymentCategory === 'Credit')
          .map((pt) => pt.paymentCategory)
      );
      setUsedSingletons(taken);
      // If the default 'Other' category was pre-selected but Cash/Credit aren't taken,
      // keep 'Other'; just ensure we don't start on a disabled option.
      if (taken.has('Other')) {
        // 'Other' is never blocked; nothing to do
      }
    }).catch(() => {
      // Silently ignore — backend will enforce on submit
    });
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      sortOrder: parseInt(e.target.value) || 0,
    }));
  };

  const validate = (): boolean => {
    if (!formData.code.trim()) {
      setError(t('paymentType.codeRequired'));
      return false;
    }
    if (!formData.name.trim()) {
      setError(t('paymentType.nameRequired'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;

    setLoading(true);
    try {
      await paymentTypeService.createPaymentType(formData);
      navigate('/settings/payment-types', {
        state: { message: t('paymentType.createSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('paymentType.failedToCreate')).message);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/settings/payment-types')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('paymentType.createTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/settings/payment-types')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="create-content">
          {/* Main form */}
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('paymentType.details')}</h2>
              </div>
              <div className="create-card-body">
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">{t('common.loading')}</span>
                    </div>
                  </div>
                ) : (
                <form id="page-form" onSubmit={handleSubmit}>

                  {/* Usage Scope */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('paymentType.usageScope', 'Usage Scope')}
                      <span className="required">*</span>
                    </label>
                    <div className="ptf-category-cards">
                      {SCOPE_OPTIONS.map((opt) => {
                        const selected = formData.usageScope === opt.value;
                        return (
                          <label
                            key={opt.value}
                            className={['ptf-category-card', selected ? 'ptf-category-card--active' : ''].join(' ')}
                            style={{ '--cat-color': opt.color } as React.CSSProperties}
                          >
                            <input
                              type="radio"
                              name="usageScope"
                              value={opt.value}
                              checked={selected}
                              onChange={() => setFormData((p) => ({ ...p, usageScope: opt.value }))}
                              className="ptf-category-radio"
                            />
                            <span className="ptf-category-icon">{opt.icon}</span>
                            <div className="ptf-category-body">
                              <span className="ptf-category-label">{opt.label}</span>
                              <span className="ptf-category-hint">{opt.hint}</span>
                            </div>
                            {selected && <span className="ptf-category-check">✓</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Payment Category */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('paymentType.paymentCategory')}
                      <span className="required">*</span>
                    </label>
                    <div className="ptf-category-cards">
                      {CATEGORY_OPTIONS.map((opt) => {
                        const selected  = formData.paymentCategory === opt.value;
                        const isTaken   = usedSingletons.has(opt.value);   // Cash/Credit already exists
                        const disabled  = isTaken;
                        return (
                          <label
                            key={opt.value}
                            className={[
                              'ptf-category-card',
                              selected  ? 'ptf-category-card--active'   : '',
                              disabled  ? 'ptf-category-card--disabled' : '',
                            ].join(' ')}
                            style={{ '--cat-color': opt.color } as React.CSSProperties}
                          >
                            <input
                              type="radio"
                              name="paymentCategory"
                              value={opt.value}
                              checked={selected}
                              disabled={disabled}
                              onChange={() => !disabled && setFormData((p) => ({ ...p, paymentCategory: opt.value }))}
                              className="ptf-category-radio"
                            />
                            <span className="ptf-category-icon">{opt.icon}</span>
                            <div className="ptf-category-body">
                              <span className="ptf-category-label">{opt.label}</span>
                              <span className="ptf-category-hint">
                                {isTaken
                                  ? `⚠️ An active ${opt.label} type already exists. Only one is allowed.`
                                  : opt.hint}
                              </span>
                            </div>
                            {isTaken  && <span className="ptf-category-locked">🔒</span>}
                            {!isTaken && selected && <span className="ptf-category-check">✓</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Code */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('paymentType.code')}
                      <span className="required">*</span>
                      <span className="char-counter">{formData.code.length}/10</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder="e.g. CASH, VISA"
                      maxLength={10}
                      style={{ textTransform: 'uppercase' }}
                    />
                    <small className="form-helper">{t('paymentType.codeHelper')}</small>
                  </div>

                  {/* Name */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('paymentType.name')}
                      <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/20</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('paymentType.namePlaceholder')}
                      maxLength={20}
                    />
                    <small className="form-helper">{t('paymentType.nameHelper')}</small>
                  </div>

                  {/* Description */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('common.description')}
                      <span className="char-counter">{(formData.description || '').length}/200</span>
                    </label>
                    <textarea
                      className="form-textarea"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder={t('paymentType.descriptionPlaceholder')}
                      rows={3}
                      maxLength={200}
                    />
                  </div>

                  {/* Sort Order */}
                  <div className="form-group form-group--half">
                    <label className="form-label">{t('paymentType.sortOrder')}</label>
                    <input
                      type="number"
                      className="form-input"
                      name="sortOrder"
                      value={formData.sortOrder}
                      onChange={handleNumberChange}
                      min={0}
                      max={9999}
                      style={{ maxWidth: 120 }}
                    />
                    <small className="form-helper">{t('paymentType.sortOrderHelper')}</small>
                  </div>

                  {/* Is Default */}
                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        name="isDefault"
                        checked={formData.isDefault}
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                      />
                    <label className="form-label" htmlFor="isDefault"
                      style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}>
                      {t('paymentType.setAsDefault')}
                    </label>
                    </div>
                    <small className="form-helper">{t('paymentType.isDefaultHelper')}</small>
                  </div>

                </form>
                )}
              </div>
            </div>
          </div>
          <div className="create-sidebar">
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('common.info')}</h3>
              </div>
              <div className="create-card-body">
                <ul className="create-help-list">
                  <li>{t('paymentType.infoCode')}</li>
                  <li>{t('paymentType.infoDefault')}</li>
                  <li>{t('paymentType.infoSort')}</li>
                </ul>
              </div>
            </div>
          </div>      
        </div>
      </div>
    </Layout>
  );
};

export default PaymentTypeCreatePage;
